import { formatarCno } from '@raiox/domain';
import { trpc } from '../lib/trpc.js';
import { Carregando, Erro } from './Estados.js';
import { competencia as fmtCompetencia, moeda, moedaOuTraco, numero, percentual } from '../lib/formato.js';
import type { DocumentoDetalhe } from '../lib/tipos.js';

function Kv({ k, v }: { k: string; v: string }) {
  return (
    <div>
      <div className="k">{k}</div>
      <div className="mono">{v}</div>
    </div>
  );
}

export function DetalheDocumento({ id }: { id: string }) {
  const consulta = trpc.documentos.obter.useQuery({ id });

  if (consulta.isPending) return <Carregando linhas={3} rotulo="Carregando o documento" />;
  if (consulta.error) return <Erro erro={consulta.error} aoTentarDeNovo={() => void consulta.refetch()} />;

  const d: DocumentoDetalhe = consulta.data;
  // Produto e serviço não têm as mesmas colunas: NF-e/NFC-e/CT-e mostram
  // NCM, CFOP e ICMS; NFS-e mostra base e alíquota de ISS.
  const mostraProduto = d.tipo === 'NF-e' || d.tipo === 'NFC-e' || d.tipo.startsWith('CT-e');

  return (
    <>
      <div className="kvs">
        <Kv k="Chave / verificação" v={d.chave || '—'} />
        <Kv k="Competência" v={fmtCompetencia(d.competencia)} />
        {d.natureza ? <Kv k="Natureza" v={d.natureza} /> : null}
        {d.itemServico ? <Kv k="Item de serviço" v={d.itemServico} /> : null}
        {d.tributos.aliqIss ? (
          <Kv
            k="Alíquota ISS"
            v={`${percentual(d.tributos.aliqIss)}${d.issEstimado ? ' · ISS calculado' : ''}`}
          />
        ) : null}
        {d.art ? <Kv k="ART" v={d.art} /> : null}
        {d.emitente.mun ? <Kv k="Município" v={d.emitente.mun} /> : null}
        {d.cnos.length > 0 ? <Kv k="CNO" v={d.cnos.map(formatarCno).join(' · ')} /> : null}
      </div>

      {d.descricaoServico ? (
        <p style={{ margin: '0 0 12px', fontSize: 12, color: 'var(--ink-2)', maxWidth: '100ch' }}>
          {d.descricaoServico}
        </p>
      ) : null}

      {d.itens.length > 0 ? (
        <div style={{ overflowX: 'auto' }}>
          <table>
            <thead>
              <tr>
                <th>#</th>
                <th>Descrição</th>
                {mostraProduto ? (
                  <>
                    <th>NCM</th>
                    <th>CFOP</th>
                    <th className="r">Qtd</th>
                  </>
                ) : null}
                <th className="r">Valor</th>
                {mostraProduto ? (
                  <>
                    <th>CST</th>
                    <th className="r">BC ICMS</th>
                    <th className="r">ICMS</th>
                    <th className="r">IPI</th>
                  </>
                ) : null}
                <th className="r">PIS</th>
                <th className="r">COFINS</th>
                {mostraProduto ? null : (
                  <>
                    <th className="r">BC ISS</th>
                    <th className="r">ISS</th>
                  </>
                )}
                <th>CST IBS/CBS</th>
                <th className="r">BC IBS/CBS</th>
                <th className="r">IBS UF</th>
                <th className="r">IBS Mun</th>
                <th className="r">CBS</th>
              </tr>
            </thead>
            <tbody>
              {d.itens.map((item, indice) => (
                <tr key={`${item.numero}-${indice}`}>
                  <td className="num">{item.numero}</td>
                  <td>
                    <span className="trunc" style={{ maxWidth: 280 }} title={item.descricao}>
                      {item.descricao}
                    </span>
                  </td>
                  {mostraProduto ? (
                    <>
                      <td className="num">{item.ncm}</td>
                      <td className="num">{item.cfop}</td>
                      <td className="num r">{item.quantidade ? numero(item.quantidade, 4) : ''}</td>
                    </>
                  ) : null}
                  <td className="num r strong">{moeda(item.valor)}</td>
                  {mostraProduto ? (
                    <>
                      <td className="num">{item.cstIcms}</td>
                      <td className="num r">{moedaOuTraco(item.bcIcms)}</td>
                      <td className="num r">{moedaOuTraco(item.icms)}</td>
                      <td className="num r">{moedaOuTraco(item.ipi)}</td>
                    </>
                  ) : null}
                  <td className="num r">{moedaOuTraco(item.pis)}</td>
                  <td className="num r">{moedaOuTraco(item.cofins)}</td>
                  {mostraProduto ? null : (
                    <>
                      <td className="num r">{moedaOuTraco(item.bcIss)}</td>
                      <td className="num r">{moedaOuTraco(item.iss)}</td>
                    </>
                  )}
                  <td className="num">{item.cstRtc}</td>
                  <td className="num r">{moedaOuTraco(item.bcRtc)}</td>
                  <td className="num r">{moedaOuTraco(item.ibsUF)}</td>
                  <td className="num r">{moedaOuTraco(item.ibsMun)}</td>
                  <td className="num r">{moedaOuTraco(item.cbs)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      {d.alertas.length > 0 ? (
        <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 6 }}>
          {d.alertas.map((a, i) => (
            <div key={`${a.codigo}-${i}`} style={{ fontSize: 12 }}>
              <span className={`badge ${a.severidade}`}>{a.codigo}</span>{' '}
              <span style={{ color: 'var(--ink-2)' }}>{a.mensagem}</span>
            </div>
          ))}
        </div>
      ) : null}

      {d.observacoes ? (
        <p style={{ margin: '12px 0 0', fontSize: 11.5, color: 'var(--ink-3)', maxWidth: '110ch' }}>
          {d.observacoes}
        </p>
      ) : null}
    </>
  );
}
